/** @jsxImportSource @emotion/react */
import Dropdown from 'components/atoms/Dropdown'
import Pagination from 'components/atoms/Pagination'
import { BillingEraTransactions } from 'components/lab/billingEraReports/BillingEraTransactions'
import { ERA_FILE_HEADER_COLUMNS, EraPaymentCells } from 'components/lab/billingEraReports/EraPaymentColumns'
import { EraArchiveResultModal } from 'components/lab/billingEraReports/EraArchiveResultModal'
import { BulkEraUploadModal } from 'components/lab/billingEraReports/BulkEraUploadModal'
import { ERA_CLAIM_OUTCOME_OPTIONS, ERA_TEST_TYPE_OPTIONS } from 'components/lab/billingEraReports/constants'
import { EraUploadSummaryModal } from 'components/lab/billingEraReports/EraUploadSummaryModal'
import { EraArchiveOutcome, EraClaimSelection } from 'components/lab/billingEraReports/types'
import { useBulkEraUpload } from 'components/lab/billingEraReports/useBulkEraUpload'
import { useEraClaimSelection } from 'components/lab/billingEraReports/useEraClaimSelection'
import { confirm } from 'components/molecules/Confirmation'
import { downloadApiFile, downloadEobPdf } from 'helpers/eob'
import { useAppDispatch } from 'hooks/redux'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { bulkEraUploadOutcome, describeEraFileRejection, ERA_ACCEPTED_FILES } from './eraUpload'
import { baseApi } from 'store/services/baseApi'
import {
  useArchiveBillingEraClaimsMutation,
  useGetBillingEraReportsQuery,
  useUploadEraReportMutation,
} from 'store/services/billingCharges'
import {
  BillingEraReportsListQuery,
  EraClaimOutcome,
  EraImportStatus,
  EraImportSummary,
  EraReportRecord,
  TestCode,
} from 'store/types'
import { Button, Form } from 'react-bootstrap'
import Input from 'components/atoms/Input'
import Select from 'components/atoms/Select'
import Upload from 'components/atoms/Upload'
import { useDebounce } from 'use-debounce'
import DateTimePicker from 'components/atoms/DateTimePicker'
import { TimeWrapper } from 'components/lab/orderEdit/PsgHstWorkflow/FollowUpForm'
import ReactSelect from 'react-select'
import { useGetLabUsersQuery } from 'store/services/users'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from 'components/lab/user/permissions'
import { formatEasternDateTime } from 'helpers/dates'

const Dot = ({ color = '#29C788' }: { color?: string }) => (
  <span
    css={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: 8,
      background: color,
      marginRight: 6,
    }}
  />
)

export const EraReport = ({
  record,
  query,
  selection,
}: {
  record: EraReportRecord
  query: BillingEraReportsListQuery
  selection?: EraClaimSelection
}) => {
  const [open, setOpen] = React.useState(false)
  const [eobLoading, setEobLoading] = React.useState(false)
  const [downloadLoading, setDownloadLoading] = React.useState(false)
  const payments = record.payments || []
  // The file, its chevron and its actions span one grid row per payment.
  const fileRows = `1 / span ${Math.max(payments.length, 1)}`

  return (
    <div className="mb-3 border rounded">
      <div
        className="p-3 bg-light"
        onClick={() => setOpen((p) => !p)}
        css={{
          cursor: 'pointer',
          display: 'grid',
          gridTemplateColumns: ERA_FILE_HEADER_COLUMNS,
          columnGap: 12,
          rowGap: 8,
          alignItems: 'center',
        }}>
        <i
          className={`fa ${open ? 'fa-chevron-down' : 'fa-chevron-right'}`}
          css={{ gridColumn: 1, gridRow: fileRows }}
        />
        <div css={{ gridColumn: 2, gridRow: fileRows, minWidth: 0 }}>
          <div
            title={record.filename}
            css={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {record.filename}
          </div>
          <div
            className="text-muted"
            css={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {formatEasternDateTime(record.createdAt)} • {record.claimAmount} claims
          </div>
        </div>
        {payments.map((payment, index) => (
          <EraPaymentCells
            key={payment.id}
            payment={payment}
            row={index + 1}
            number={payments.length > 1 ? index + 1 : undefined}
          />
        ))}
        <div
          className="d-flex flex-column align-items-end"
          css={{ gridColumn: '-2 / -1', gridRow: fileRows, minWidth: 0 }}>
          <div className="d-flex">
            <Button
              size="sm"
              variant="outline-dark"
              className="mr-2"
              disabled={eobLoading}
              onClick={async (e) => {
                e.stopPropagation()
                setEobLoading(true)
                await downloadEobPdf(`/lab/billing/era-reports/${record.id}/eob`, `EOB_ERA_${record.id}.pdf`)
                setEobLoading(false)
              }}>
              {eobLoading ? 'Print EOB…' : 'Print EOB'}
            </Button>
            {/* a disabled button swallows hover, so the tooltip sits on the wrapper */}
            <span
              title={record.hasOriginalFile ? undefined : 'Original not retained'}
              onClick={(e) => e.stopPropagation()}>
              <Button
                size="sm"
                variant="outline-dark"
                disabled={!record.hasOriginalFile || downloadLoading}
                onClick={async () => {
                  setDownloadLoading(true)
                  await downloadApiFile(
                    `/lab/billing/era-reports/${record.id}/original`,
                    record.filename,
                    'application/octet-stream',
                    'ERA file downloaded',
                    'Failed to download the ERA file',
                  )
                  setDownloadLoading(false)
                }}>
                {downloadLoading ? 'Download…' : 'Download'}
              </Button>
            </span>
          </div>
          <div
            className="text-muted mt-1"
            title={record.id}
            css={{ fontSize: 11, maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {record.id}
          </div>
        </div>
      </div>

      {open && <BillingEraTransactions eraId={record.id} query={query} selection={selection} />}
    </div>
  )
}

type PostedSearchParams = {
  patientName: string
  plan: string
  location: string
  dateFrom: Date | null
  dateTo: Date | null
  postedBy: string
  checkNumber: string
  testType: TestCode | ''
  outcome: EraClaimOutcome | ''
}

export const BillingEraReportsListing = () => {
  const [search, setSearch] = useState('')
  const [pageSize, setPageSize] = useState(20)
  const [currentPage, setCurrentPage] = useState(0)

  const [debouncedSearch] = useDebounce(search, 500)
  const [tab, setTab] = useState<'pending' | 'posted'>('pending')
  const [pendingTestType, setPendingTestType] = useState<TestCode | ''>('')
  const [showFutureCheckDates, setShowFutureCheckDates] = useState(false)

  const [postedSearchParams, setPostedSearchParams] = useState<PostedSearchParams>({
    patientName: '',
    plan: '',
    location: '',
    dateFrom: null,
    dateTo: null,
    postedBy: '',
    checkNumber: '',
    testType: '',
    outcome: '',
  })
  const [appliedPostedSearchParams, setAppliedPostedSearchParams] =
    useState<PostedSearchParams | null>(null)
  const [shouldFetchPosted, setShouldFetchPosted] = useState(false)
  const [userSearch, setUserSearch] = useState('')
  const [debouncedUserSearch] = useDebounce(userSearch, 500)

  const isPostedSearchEmpty =
    !postedSearchParams.patientName &&
    !postedSearchParams.plan &&
    !postedSearchParams.location &&
    !postedSearchParams.dateFrom &&
    !postedSearchParams.dateTo &&
    !postedSearchParams.postedBy &&
    !postedSearchParams.checkNumber &&
    !postedSearchParams.testType &&
    !postedSearchParams.outcome

  const { data: labUsers } = useGetLabUsersQuery(
    debouncedUserSearch.length >= 2
      ? {
          search: debouncedUserSearch,
          page: 0,
          size: 50,
        }
      : skipToken,
  )

  const { data, isLoading, refetch } = useGetBillingEraReportsQuery(
    {
      page: currentPage,
      size: pageSize,
      search: tab === 'pending' ? debouncedSearch : undefined,
      status: tab === 'pending' ? 'PENDING' : 'POSTED',
      testType: (tab === 'pending' && pendingTestType) || undefined,
      includeFutureCheckDates: (tab === 'pending' && showFutureCheckDates && 'true') || undefined,
      ...(tab === 'posted' &&
        shouldFetchPosted &&
        appliedPostedSearchParams && {
          patientName: appliedPostedSearchParams.patientName,
          plan: appliedPostedSearchParams.plan,
          location: appliedPostedSearchParams.location,
          dateFrom: appliedPostedSearchParams.dateFrom?.toISOString(),
          dateTo: appliedPostedSearchParams.dateTo?.toISOString(),
          postedBy: appliedPostedSearchParams.postedBy,
          checkNumber: appliedPostedSearchParams.checkNumber,
          testType: appliedPostedSearchParams.testType || undefined,
          outcome: appliedPostedSearchParams.outcome || undefined,
        }),
    },
    {
      skip: tab === 'posted' && !shouldFetchPosted,
      refetchOnFocus: tab === 'pending',
    },
  )

  const canPostInsurancePayments = useLabPermission(UserPermission.POST_INSURANCE_PAYMENTS)
  const [uploadEraReport, { isLoading: isUploading }] = useUploadEraReportMutation()
  const [uploadSummary, setUploadSummary] = useState<EraImportSummary | null>(null)
  // Several files at once: uploaded one by one, reported per file.
  const bulkUpload = useBulkEraUpload()
  const [showBulkUpload, setShowBulkUpload] = useState(false)
  const dispatch = useAppDispatch()
  const [archiveEraClaims, { isLoading: isArchiving }] = useArchiveBillingEraClaimsMutation()
  const [archiveOutcome, setArchiveOutcome] = useState<EraArchiveOutcome | null>(null)

  const claimsQuery: BillingEraReportsListQuery = {
    search: tab === 'pending' ? debouncedSearch : undefined,
    status: tab === 'pending' ? 'PENDING' : 'POSTED',
    testType: (tab === 'pending' && pendingTestType) || undefined,
    includeFutureCheckDates: (tab === 'pending' && showFutureCheckDates && 'true') || undefined,
    ...(tab === 'posted' &&
      shouldFetchPosted &&
      appliedPostedSearchParams && {
        patientName: appliedPostedSearchParams.patientName,
        plan: appliedPostedSearchParams.plan,
        location: appliedPostedSearchParams.location,
        dateFrom: appliedPostedSearchParams.dateFrom?.toISOString(),
        dateTo: appliedPostedSearchParams.dateTo?.toISOString(),
        postedBy: appliedPostedSearchParams.postedBy,
        checkNumber: appliedPostedSearchParams.checkNumber,
        testType: appliedPostedSearchParams.testType || undefined,
        outcome: appliedPostedSearchParams.outcome || undefined,
      }),
  }
  const { selection, selectedClaims, applyArchiveResult } = useEraClaimSelection(
    JSON.stringify({ claimsQuery, currentPage, pageSize }),
  )

  const handleArchiveSelected = () => {
    const claims = selectedClaims

    confirm('', {
      title: 'Are you sure?',
      description: `Are you sure you want to archive ${claims.length} claim(s)?`,
      yesAction: 'Yes',
    })
      .then(async () => {
        try {
          const result = await archiveEraClaims({ claimIds: claims.map((c) => c.id) }).unwrap()
          applyArchiveResult(result)
          setArchiveOutcome({ result, claims })
        } catch (e) {
          toast.error('There was an error archiving the selected claims')
        }
      })
      .catch(() => {})
  }

  const handleBulkUpload = async (files: File[]) => {
    setShowBulkUpload(true)

    const results = await bulkUpload.start(files)

    // One refresh for the whole batch rather than one per file.
    dispatch(baseApi.util.invalidateTags(['BillingEra']))
    setTab('pending')

    const imported = results.filter((item) => ['imported', 'partial'].includes(bulkEraUploadOutcome(item))).length
    const failed = results.filter((item) => item.state === 'failed').length
    const stopped = results.filter((item) => item.state === 'stopped').length
    const others = results.length - imported - failed - stopped
    const parts = [
      `${imported} imported`,
      others ? `${others} already uploaded or empty` : '',
      failed ? `${failed} failed` : '',
      stopped ? `${stopped} not sent` : '',
    ].filter(Boolean)

    if (failed) {
      toast.warning(`ERA upload finished: ${parts.join(', ')}`)
    } else {
      toast.success(`ERA upload finished: ${parts.join(', ')}`)
    }
  }

  const handleUpload = async (files: File[]) => {
    if (files?.length > 1) {
      await handleBulkUpload(files)
      return
    }

    const file = files?.[0]
    if (!file) return
    try {
      const summary = await uploadEraReport(file).unwrap()

      if (summary.status === EraImportStatus.IMPORTED) {
        toast.success('ERA file uploaded')
      } else {
        // duplicates, partial imports and empty files need the detail of what was skipped
        setUploadSummary(summary)
      }

      setTab('pending')
    } catch (e: any) {
      toast.error(`Failed to upload ERA file: ${e?.data?.message || e}`)
    }
  }

  useEffect(() => {
    if (tab === 'pending') {
      refetch()
    }
  }, [tab])

  return (
    <div
      css={{
        width: '100%',
        padding: 10,
      }}>
      <div className="mt-2">
        <div className={'mb-4'}>
          <div className={`d-flex justify-content-between align-items-start ${tab === 'pending' ? 'mb-0' : 'mb-4'}`}>
            <div>
              <Button
                variant={tab === 'pending' ? 'dark' : 'outline-dark'}
                className={'mr-2'}
                onClick={() => setTab('pending')}>
                Pending
              </Button>
              <Button
                variant={tab === 'posted' ? 'dark' : 'outline-dark'}
                onClick={() => setTab('posted')}>
                Posted / Parked
              </Button>
            </div>
            <div className="d-flex flex-column align-items-end" css={{ gap: 12 }}>
              {canPostInsurancePayments && (
                <div className="d-flex align-items-center" css={{ gap: 12 }}>
                  {!!selectedClaims.length && (
                    <span className="text-muted" css={{ whiteSpace: 'nowrap' }}>
                      {selectedClaims.length} selected
                    </span>
                  )}
                  <Button
                    variant="outline-secondary"
                    disabled={!selectedClaims.length || isArchiving}
                    onClick={handleArchiveSelected}>
                    {isArchiving
                      ? 'Archiving…'
                      : `Archive selected${selectedClaims.length ? ` (${selectedClaims.length})` : ''}`}
                  </Button>
                  {/* A batch keeps going with its dialog hidden; this brings it back. */}
                  {!showBulkUpload && bulkUpload.items.length > 0 && (
                    <Button variant="outline-primary" onClick={() => setShowBulkUpload(true)}>
                      {bulkUpload.running
                        ? `Uploading ${bulkUpload.items.filter((item) => item.state === 'done' || item.state === 'failed').length} of ${bulkUpload.items.length}…`
                        : 'Last upload results'}
                    </Button>
                  )}
                  <Upload
                    className="btn btn-primary"
                    label={isUploading || bulkUpload.running ? 'Uploading ERA files…' : 'Upload ERA files'}
                    multiple
                    disabled={isUploading || bulkUpload.running}
                    accept={ERA_ACCEPTED_FILES}
                    handleUpload={handleUpload}
                    onRejected={(rejections) => toast.error(describeEraFileRejection(rejections))}
                  />
                </div>
              )}
              {tab === 'pending' && (
                <Form.Check
                  id="show-future-check-dates"
                  type="checkbox"
                  className="pl-0 d-flex align-items-center"
                  css={{
                    whiteSpace: 'nowrap',
                    '& .form-check-input': { position: 'static', margin: '0 6px 0 0' },
                    '& .form-check-label': { marginBottom: 0 },
                  }}
                  label="Show future check dates"
                  checked={showFutureCheckDates}
                  onChange={(e) => {
                    setCurrentPage(0)
                    setShowFutureCheckDates(e.target.checked)
                  }}
                />
              )}
            </div>
          </div>
          {tab === 'pending' && (
            <div className={'d-flex align-items-center justify-content-between flex-wrap'} css={{ rowGap: 16 }}>
              <div className={'d-flex align-items-end'}>
                <div
                  css={{
                    width: 780,
                    display: 'flex',
                    alignItems: 'end',
                    '& #search': {
                      width: 400,
                    },
                  }}>
                  <Input
                    value={search}
                    placeholder="Payer name, Claim Identifier, Check / Trace #, first name, last name"
                    label="Search ERA reports"
                    name="search"
                    handleChange={(e) => {
                      setCurrentPage(0)
                      setSearch(e.target.value)
                    }}
                  />
                  <Select
                    value={pendingTestType}
                    name="testType"
                    label="Test type"
                    wrapperClassName="ml-4 mb-0"
                    handleChange={(e) => {
                      setCurrentPage(0)
                      setPendingTestType(e.target.value as TestCode | '')
                    }}
                    defaultLabel="Any"
                    options={ERA_TEST_TYPE_OPTIONS}
                  />
                  <Dropdown
                    initialValue={pageSize.toString()}
                    onClick={(v) => {
                      setCurrentPage(0)
                      setPageSize(Number(v))
                    }}
                    buttonTypeClass="btn-outline-primary ml-4"
                    options={[
                      { label: '12' },
                      {
                        label: '20',
                      },
                      {
                        label: '50',
                      },
                      {
                        label: '100',
                      },
                    ]}
                    additionalText="Records"
                  />
                </div>
              </div>
              <div className={'d-flex align-items-center flex-wrap'} css={{ gap: 20 }}>
                <div>
                  <Dot color={'#29C788'} /> <span className={'text-muted'}>Postable</span>
                </div>
                <div>
                  <Dot color={'#FF3A5B'} /> <span className={'text-muted'}>Denied</span>
                </div>
                <div>
                  <Dot color={'#17A2B8'} /> <span className={'text-muted'}>Patient Resp.</span>
                </div>
                <div>
                  <Dot color={'#FEBF4C'} /> <span className={'text-muted'}>Review</span>
                </div>
                <div>
                  <Dot color={'#3CAEFF'} /> <span className={'text-muted'}>Posted</span>
                </div>
                <div>
                  <Dot color={'#9370FF'} /> <span className={'text-muted'}>Reversals</span>
                </div>
                <div>
                  <Dot color={'#C4C4C4'} /> <span className={'text-muted'}>Non-Postable / Not Our Claim</span>
                </div>
              </div>
            </div>
          )}

          {tab === 'posted' && (
            <div>
              <div className="row">
                <div className="col">
                  <Input
                    value={postedSearchParams.patientName}
                    placeholder="Patient or policyholder name"
                    label="Patient Name"
                    name="patientName"
                    handleChange={(e) =>
                      setPostedSearchParams((prev) => ({ ...prev, patientName: e.target.value }))
                    }
                  />
                </div>
                <div className="col">
                  <Input
                    value={postedSearchParams.plan}
                    placeholder="Plan"
                    label="Plan"
                    name="plan"
                    handleChange={(e) =>
                      setPostedSearchParams((prev) => ({ ...prev, plan: e.target.value }))
                    }
                  />
                </div>
                <div className="col">
                  <label className="text-gray" htmlFor="location">
                    Location
                  </label>
                  <ReactSelect
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 10 }) }}
                    className="basic-single w-100"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    placeholder="Select Location"
                    name="location"
                    onChange={(item) =>
                      setPostedSearchParams((prev) => ({ ...prev, location: item?.value || '' }))
                    }
                    options={[
                      { value: '11', label: '11: Office' },
                      { value: '12', label: '12: Home' },
                      { value: '81', label: '81: Independent Laboratory' },
                    ]}
                    value={
                      postedSearchParams.location
                        ? [
                            { value: '11', label: '11: Office' },
                            { value: '12', label: '12: Home' },
                            { value: '81', label: '81: Independent Laboratory' },
                          ].find((o) => o.value === postedSearchParams.location)
                        : null
                    }
                  />
                </div>
                <div className="col">
                  <label className="text-gray" htmlFor="dateFrom">
                    Posted date from
                  </label>
                  <TimeWrapper>
                    <DateTimePicker
                      value={postedSearchParams.dateFrom}
                      handleChange={(d) =>
                        setPostedSearchParams((prev) => ({ ...prev, dateFrom: d }))
                      }
                      showTimeSelect={false}
                      isClearable
                      dateFormat="MMMM d, yyyy"
                    />
                  </TimeWrapper>
                </div>
                <div className="col">
                  <label className="text-gray" htmlFor="dateTo">
                    Posted date to
                  </label>
                  <TimeWrapper>
                    <DateTimePicker
                      value={postedSearchParams.dateTo}
                      handleChange={(d) =>
                        setPostedSearchParams((prev) => ({ ...prev, dateTo: d }))
                      }
                      showTimeSelect={false}
                      isClearable
                      dateFormat="MMMM d, yyyy"
                    />
                  </TimeWrapper>
                </div>
                <div className="col">
                  <label className="text-gray" htmlFor="postedBy">
                    Posted by
                  </label>
                  <ReactSelect
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 10 }) }}
                    className="basic-single w-100"
                    classNamePrefix="select"
                    isClearable
                    isSearchable
                    placeholder="Select User"
                    name="postedBy"
                    onChange={(item) =>
                      setPostedSearchParams((prev) => ({ ...prev, postedBy: item?.value || '' }))
                    }
                    onInputChange={(v) => setUserSearch(v)}
                    inputValue={userSearch}
                    options={
                      Array.isArray(labUsers?.items)
                        ? labUsers?.items?.map((u) => ({
                            value: u.id.toString(),
                            label: `${u.id} - ${u.firstName} ${u.lastName}`,
                          }))
                        : []
                    }
                    value={
                      postedSearchParams.postedBy && Array.isArray(labUsers?.items)
                        ? labUsers?.items
                            .map((u) => ({
                              value: u.id.toString(),
                              label: `${u.id} - ${u.firstName} ${u.lastName}`,
                            }))
                            .find((o) => o.value === postedSearchParams.postedBy)
                        : null
                    }
                  />
                </div>
                <div className="col">
                  <Input
                    value={postedSearchParams.checkNumber}
                    placeholder="Check number"
                    label="Check number"
                    name="checkNumber"
                    handleChange={(e) =>
                      setPostedSearchParams((prev) => ({ ...prev, checkNumber: e.target.value }))
                    }
                  />
                </div>
                <div className="col">
                  <Select
                    value={postedSearchParams.testType}
                    name="testType"
                    label="Test type"
                    handleChange={(e) =>
                      setPostedSearchParams((prev) => ({ ...prev, testType: e.target.value as TestCode | '' }))
                    }
                    defaultLabel="Any"
                    options={ERA_TEST_TYPE_OPTIONS}
                  />
                </div>
                <div className="col">
                  <Select
                    value={postedSearchParams.outcome}
                    name="outcome"
                    label="Outcome"
                    handleChange={(e) =>
                      setPostedSearchParams((prev) => ({ ...prev, outcome: e.target.value as EraClaimOutcome | '' }))
                    }
                    defaultLabel="Any"
                    options={ERA_CLAIM_OUTCOME_OPTIONS}
                  />
                </div>
                <div className="col mb-3 pt-4 d-flex align-items-end">
                  <Button
                    variant="primary"
                    disabled={isPostedSearchEmpty}
                    onClick={() => {
                      setCurrentPage(0)
                      setAppliedPostedSearchParams(postedSearchParams)
                      setShouldFetchPosted(true)
                    }}>
                    Search
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        {tab === 'posted' && !shouldFetchPosted && (
          <div className="p-3">Please enter search criteria and click Search to view results</div>
        )}
        {isLoading && <div className="p-3">Loading...</div>}
        {!isLoading &&
          (tab !== 'posted' || shouldFetchPosted) &&
          Array.isArray(data?.items) &&
          data?.items?.map((rec: EraReportRecord) => (
            <EraReport
              key={rec.id}
              record={rec}
              query={claimsQuery}
              selection={canPostInsurancePayments ? selection : undefined}
            />
          ))}
        {!isLoading && Array.isArray(data?.items) && data?.items?.length === 0 && (
          <div className="p-3">No records found</div>
        )}
      </div>
      <Pagination
        className="pagination-bar"
        currentPage={currentPage}
        totalCount={data?.totalItems || 1}
        pageSize={pageSize}
        siblingCount={1}
        setPageSize={setPageSize}
        onPageChange={setCurrentPage}
      />
      <EraUploadSummaryModal summary={uploadSummary} onClose={() => setUploadSummary(null)} />
      <BulkEraUploadModal
        show={showBulkUpload}
        items={bulkUpload.items}
        running={bulkUpload.running}
        onStop={bulkUpload.stop}
        onHide={() => setShowBulkUpload(false)}
      />
      <EraArchiveResultModal outcome={archiveOutcome} onClose={() => setArchiveOutcome(null)} />
    </div>
  )
}
